import '@/global.css';

import { PortalHost } from '@rn-primitives/portal';
import { useFonts } from 'expo-font';
import { router, Stack, type Href } from 'expo-router';
import { ThemeProvider } from 'expo-router/react-navigation';
import * as SplashScreen from 'expo-splash-screen';
import { StatusBar } from 'expo-status-bar';
import { useColorScheme } from 'nativewind';
import * as React from 'react';
import { LogBox, Platform } from 'react-native';
import { SafeAreaProvider } from 'react-native-safe-area-context';

import { AndroidStatusBarInset } from '@/components/android-status-bar-inset';
import { AnalyticsProvider } from '@/components/analytics-provider';
import { SessionProvider } from '@/components/session-provider';
import { TenantProvider } from '@/components/tenant-provider';
import { PushNotificationRegistration } from '@/components/push-notification-registration';
import { ReleaseFeatureRouteGuard } from '@/components/release-feature-route-guard';
import { DevTimeController } from '@/components/dev-time-controller';
import { BRAND } from '@sixam/config/brand';
import { MOTION } from '@sixam/config/motion';
import { NAV_THEME } from '@/lib/theme';
import { DevClockProvider } from '@/lib/dev-clock';
import { DevReleaseFeatureProvider } from '@/lib/dev-release-features';
import { supabase } from '@sixam/supabase/client';

export { ErrorBoundary } from 'expo-router';

/*
  Silences one specific dev notification, not the tool.

  expo-router asks the OS for the initial deep link on launch and updates state
  when that promise resolves, which can land before its own component has
  finished mounting. It comes from expo-router/build/fork/useLinking.native.js,
  so there is nothing to fix here, and the bar it raises sits over the tab bar
  on every launch. Every other warning and error still shows.
*/
LogBox.ignoreLogs(["Can't perform a React state update on a component that hasn't mounted yet"]);

void SplashScreen.preventAutoHideAsync();

SplashScreen.setOptions({
  duration: 220,
  fade: true,
});

function useNotificationDeepLinks() {
  React.useEffect(() => {
    if (Platform.OS === 'web') {
      return undefined;
    }

    let mounted = true;
    let subscription: { remove: () => void } | undefined;

    async function subscribe() {
      const Notifications = await import('expo-notifications');

      Notifications.setNotificationHandler({
        handleNotification: async () => ({
          shouldPlaySound: false,
          shouldSetBadge: false,
          shouldShowBanner: true,
          shouldShowList: true,
        }),
      });

      function redirect(notification: import('expo-notifications').Notification) {
        const url = notification.request.content.data?.url;
        if (typeof url === 'string') {
          router.push(url as Href);
          Notifications.clearLastNotificationResponse();
        }
      }

      const lastResponse = Notifications.getLastNotificationResponse();
      if (mounted && lastResponse?.notification) {
        redirect(lastResponse.notification);
      }

      if (!mounted) {
        return;
      }

      subscription = Notifications.addNotificationResponseReceivedListener((response) => {
        redirect(response.notification);
      });
    }

    void subscribe();

    return () => {
      mounted = false;
      subscription?.remove();
    };
  }, []);
}

function useAppleCredentialRevocation() {
  React.useEffect(() => {
    if (Platform.OS !== 'ios') return undefined;

    let active = true;
    let subscription: { remove: () => void } | undefined;
    void import('expo-apple-authentication').then((AppleAuthentication) => {
      if (!active) return;
      subscription = AppleAuthentication.addRevokeListener(() => {
        void supabase.auth.signOut({ scope: 'local' });
      });
    });

    return () => {
      active = false;
      subscription?.remove();
    };
  }, []);
}

export default function RootLayout() {
  useNotificationDeepLinks();
  useAppleCredentialRevocation();
  const { colorScheme } = useColorScheme();
  const [fontsLoaded, fontError] = useFonts({
    [BRAND.font.mono]: require('@/assets/fonts/AtkinsonHyperlegibleMono-Variable.ttf'),
  });

  React.useEffect(() => {
    if (fontsLoaded || fontError) {
      void SplashScreen.hideAsync();
    }
  }, [fontError, fontsLoaded]);

  if (!fontsLoaded && !fontError) {
    return null;
  }

  return (
    <SafeAreaProvider>
      <DevClockProvider>
        <DevReleaseFeatureProvider>
          <SessionProvider>
            <AnalyticsProvider>
              <ReleaseFeatureRouteGuard>
                <TenantProvider>
                  <PushNotificationRegistration />
                  <ThemeProvider value={NAV_THEME[colorScheme ?? 'light']}>
                    {/* Android always gets light icons because the strip behind them is dark ink. */}
                    <StatusBar
                      style={Platform.OS === 'android' || colorScheme === 'dark' ? 'light' : 'dark'}
                    />
                    <AndroidStatusBarInset>
                      <Stack screenOptions={{ headerShown: false, ...MOTION.stack }}>
                        <Stack.Screen name="index" />
                        <Stack.Screen name="privacy" />
                        <Stack.Screen name="early-access" />
                        <Stack.Screen name="account-deletion" />
                        <Stack.Screen name="route-preview" />
                        <Stack.Screen name="support" />
                        <Stack.Screen name="terms" />
                        <Stack.Screen name="(public)" />
                        <Stack.Screen name="(authenticated)" />
                        <Stack.Screen name="[tenant]" />
                      </Stack>
                      <DevTimeController />
                      <PortalHost />
                    </AndroidStatusBarInset>
                  </ThemeProvider>
                </TenantProvider>
              </ReleaseFeatureRouteGuard>
            </AnalyticsProvider>
          </SessionProvider>
        </DevReleaseFeatureProvider>
      </DevClockProvider>
    </SafeAreaProvider>
  );
}
